import { useEffect, useState } from 'react';
import axiosNest from '../../api/axiosNest';

/**
 * Powody przekazania do Trade i zwrotu do FTL — odpowiada
 * `/trade-transfer-reasons` w API (makieta back office v2, A6: BR-FTL-03,
 * BR-TRD-11; przełącznik `tradeTransferReasons`, 20.10.2026). Listy to
 * wartości robocze z makiety — zmienia je administrator.
 */

/** Przełącznik funkcji — klucz z rejestru API (dane, nie przemianowujemy). */
export const FEATURE_TRADE_TRANSFER_REASONS = 'tradeTransferReasons';

/** Rodzaj przejścia — dane z API (`handover` | `return`). */
export type TradeTransferKind = 'handover' | 'return';

export const TRADE_TRANSFER_KIND_LABELS: Record<TradeTransferKind, string> = {
  handover: 'Przekazanie do Trade',
  return: 'Zwrot z Trade do FTL',
};

export const TRADE_TRANSFER_KIND_CHOICES = [
  { id: 'handover', name: TRADE_TRANSFER_KIND_LABELS.handover },
  { id: 'return', name: TRADE_TRANSFER_KIND_LABELS.return },
];

export function isTradeTransferKind(v: unknown): v is TradeTransferKind {
  return v === 'handover' || v === 'return';
}

/** Klucz filtra listy zleceń — kontrakt z API (`trade-filters.ts`). */
export const TRADE_REASON_FILTER = 'tradeReason';

export interface TradeTransferReason {
  id: number;
  code: string;
  kind: string;
  label: string;
  requiresComment: boolean;
  active: boolean;
  sortOrder: number;
  updatedAt?: string;
  updatedByName?: string | null;
}

export interface TradeTransferReasonChange {
  id: number;
  reasonId: number | null;
  code: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string;
  changedByName: string | null;
  changedAt: string;
}

/** Nazwy pól historii dla operatora — klucze jak w API (`REASON_FIELDS`). */
export const REASON_FIELD_LABELS: Record<string, string> = {
  created: 'dodanie',
  label: 'treść',
  active: 'na liście',
  requiresComment: 'wymaga komentarza',
  sortOrder: 'kolejność',
};

const BASE = '/trade-transfer-reasons';

/**
 * Rodzaj przejścia między działami ze słownika stanów — ta sama reguła co
 * w API (`trade-transfer.ts`): wejście do Trade — przekazanie, z Trade do
 * FTL — zwrot, reszta — nic.
 */
export function transferKindOf(
  fromDepartment: string | null | undefined,
  toDepartment: string | null | undefined,
): TradeTransferKind | null {
  if (toDepartment === 'TRADE' && fromDepartment !== 'TRADE') return 'handover';
  if (fromDepartment === 'TRADE' && toDepartment === 'FTL') return 'return';
  return null;
}

/** Aktywne powody rodzaju, po kolejności — do wyboru w oknie zmiany stanu. */
export function selectableReasons(
  reasons: readonly TradeTransferReason[],
  kind: TradeTransferKind,
): TradeTransferReason[] {
  return reasons
    .filter((r) => r.kind === kind && r.active)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'pl'));
}

/** Etykieta powodu po kodzie — także wyłączonego (historia, filtr). */
export function reasonLabel(
  reasons: readonly TradeTransferReason[],
  code: unknown,
): string | null {
  if (typeof code !== 'string' || code === '') return null;
  return reasons.find((r) => r.code === code)?.label ?? null;
}

let cached: TradeTransferReason[] | null = null;
let inFlight: Promise<TradeTransferReason[]> | null = null;

export async function fetchTradeReasons(): Promise<TradeTransferReason[]> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = axiosNest
    .get<TradeTransferReason[]>(BASE)
    .then(({ data }) => {
      cached = Array.isArray(data) ? data : [];
      return cached;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Po zmianie słownika i w testach — następne użycie pobierze na nowo. */
export function forgetTradeReasons(): void {
  cached = null;
  inFlight = null;
}

/**
 * Słownik powodów raz na sesję (jak słownik stanów). `enabled = false` —
 * bez zapytania (przełącznik wyłączony: API odpowiada 404). Błąd — pusta
 * lista: okno zmiany stanu powie, że listy nie ma, zamiast zgadywać.
 */
export function useTradeReasons(enabled: boolean): TradeTransferReason[] | null {
  const [reasons, setReasons] = useState<TradeTransferReason[] | null>(
    enabled ? cached : [],
  );
  useEffect(() => {
    if (!enabled) {
      setReasons([]);
      return undefined;
    }
    let active = true;
    void fetchTradeReasons()
      .then((r) => {
        if (active) setReasons(r);
      })
      .catch(() => {
        if (active) setReasons([]);
      });
    return () => {
      active = false;
    };
  }, [enabled]);
  return reasons;
}

export async function fetchTradeReasonHistory(): Promise<TradeTransferReasonChange[]> {
  const { data } = await axiosNest.get<TradeTransferReasonChange[]>(`${BASE}/history`);
  return Array.isArray(data) ? data : [];
}

export async function createTradeReason(input: {
  kind: TradeTransferKind;
  label: string;
  requiresComment: boolean;
  reason: string;
}) {
  const { data } = await axiosNest.post(BASE, input);
  forgetTradeReasons();
  return data;
}

export async function updateTradeReason(
  id: number,
  input: {
    label?: string;
    active?: boolean;
    requiresComment?: boolean;
    sortOrder?: number;
    reason: string;
  },
) {
  const { data } = await axiosNest.patch(`${BASE}/${id}`, input);
  forgetTradeReasons();
  return data;
}

/**
 * Ostatnie przekazanie/zwrot na rekordzie zlecenia (`tradeTransfer*` z API) —
 * `null`, gdy go nie ma.
 */
export function lastTransferOf(record: Record<string, unknown> | undefined | null): {
  kind: TradeTransferKind;
  code: string | null;
  text: string | null;
  at: string | null;
} | null {
  if (!record) return null;
  const kind = record.tradeTransferKind;
  if (!isTradeTransferKind(kind)) return null;
  const str = (v: unknown) => (typeof v === 'string' && v !== '' ? v : null);
  return {
    kind,
    code: str(record.tradeTransferReasonCode),
    text: str(record.tradeTransferReason),
    at: str(record.tradeTransferAt),
  };
}
